import React, { useState, useContext, useEffect, useImperativeHandle, useRef } from "react";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import { useForm, Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { AccountContext } from "context/account";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiTagInput from "components/SuiTagInput";

import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import NumbersIcon from "@mui/icons-material/Numbers";
import EditIcon from "@mui/icons-material/Edit";
import UploadIcon from "@mui/icons-material/Upload";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import DeleteIcon from "@mui/icons-material/Delete";

import toast from "react-hot-toast";

import CategoryContext from "context/category-context";

import api from "services/api";
import moment from "moment";
import { capitalCase } from "capital-case";

import { Upload } from "antd";

import {
  uwProductClosureTypeOptions,
  uwProductCrownShapeOptions,
  uwProductVisorOptions,
} from "utils/data";
import ImageCardWrapper from "components/UpStyled/ImageCardWrapper";
import { handleUploadFiles, getStyleImages, canResizeImageUrl } from "utils/imageHelper";
import Tooltip from "@mui/material/Tooltip";

import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Download from "yet-another-react-lightbox/plugins/download";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";

const CreateProductModal = ({
  visible,
  handleClose,
  handleOk,
  isEdit = false,
  product = {},
  materials = [],
  handleRefresh = () => {},
  handleAfterOk = () => {},
  refreshDelay = 0,
}) => {
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState();
  const [editMode, setEditMode] = useState(isEdit);

  const [imgsViewerVisible, setImgsViewerVisible] = useState(false);
  const [imgsViewerCurrent, setImgsViewerCurrent] = useState(0);

  const [uwProductMaterialOptions, setUwProductMaterialOptions] = useState([]);

  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    getValues,
    reset,
    watch,
    control,
    formState: { errors },
  } = useForm();

  useState(() => {
    const options = materials.map((x) => ({
      label: capitalCase(x.name, { stripRegexp: /[^A-Z0-9\'\&\/-_]/gi }),
      value: x.name,
    }));
    setUwProductMaterialOptions(options);
  }, []);

  const findOption = (content, key) => {
    if (!key || !content?.[key]) return undefined;

    let option = uwProductCrownShapeOptions.find((x) => x.value == content?.[key]);
    if (option?.value) {
      return option;
    }
    option = uwProductVisorOptions.find((x) => x.value == content?.[key]);
    if (option?.value) {
      return option;
    }

    option = uwProductClosureTypeOptions.find((x) => x.value == content?.[key]);
    if (option?.value) {
      return option;
    }

    option = uwProductMaterialOptions.find((x) => x.value == content?.[key]);
    if (option?.value) {
      return option;
    }

    return { value: content?.[key], label: content?.[key] };
  };

  useEffect(() => {
    if (isEdit) {
      let resetPayload = {
        name: product?.name,
        code: product?.code,
        description: product?.content?.description,
        crown_shape: findOption(product?.content, "crown_shape"),
        front_panels_fabric: findOption(product?.content, "front_panels_fabric"),
        backs_and_sides_fabric: findOption(product?.content, "backs_and_sides_fabric"),
        visor_fabric: findOption(product?.content, "visor_fabric"),
        button_fabric: findOption(product?.content, "button_fabric"),
        closure_type: findOption(product?.content, "closure_type"),
        closure_color: findOption(product?.content, "closure_color"),
        num_eyelets: product?.content?.["num_eyelets"],
        crown_stitch_color: findOption(product?.content, "crown_stitch_color"),
        visor_stitch_color: findOption(product?.content, "visor_stitch_color"),
        baseball_stitch_color: findOption(product?.content, "baseball_stitch_color"),

        eyelet_color: findOption(product?.content, "eyelet_color"),

        num_visor_stitches: product?.content?.["num_visor_stitches"],

        visor_curve_or_flat: findOption(product?.content, "visor_curve_or_flat"),
        sandwich_color: product?.content?.["sandwich_color"],
        trim_color: product?.content?.["trim_color"],
      };

      if (product?.content?.images && product?.content?.images.length > 0) {
        setImages(product?.content?.images);
        setSelectedImage(product.content?.images[0]);
      }

      reset(resetPayload);
    } else {
      reset({});
    }
  }, [isEdit]);

  const handleCreateProduct = async (data) => {
    console.log(data);
    try {
      setUploading(true);
      const attachFiles = await handleUploadFiles(images, {}, "unionwear/products");
      console.log("saving");
      let payload = {
        name: data["name"],
        code: data["code"],
        content: {
          ...product?.content,
          description: data["description"],
          images: attachFiles,
          crown_shape: data["crown_shape"]?.value,
          front_panels_fabric: data["front_panels_fabric"]?.value,
          backs_and_sides_fabric: data["backs_and_sides_fabric"]?.value,
          visor_fabric: data["visor_fabric"]?.value,
          button_fabric: data["button_fabric"]?.value,
          closure_type: data["closure_type"]?.value,
          closure_color: data["closure_color"]?.value,
          num_eyelets: data["num_eyelets"],
          crown_stitch_color: data["crown_stitch_color"]?.value,
          visor_stitch_color: data["visor_stitch_color"]?.value,
          baseball_stitch_color: data["baseball_stitch_color"]?.value,

          eyelet_color: data["eyelet_color"]?.value,

          num_visor_stitches: data["num_visor_stitches"],

          visor_curve_or_flat: data["visor_curve_or_flat"]?.value,
          sandwich_color: data["sandwich_color"],
          trim_color: data["trim_color"],
        },
      };

      if (editMode) {
        if (!product.id) {
          setUploading(false);
          return toast.error("Can't find the material!");
        }
        console.log(product.id, payload);
        const newCreatedProduct = await api.uwProduct.update(product.id, payload);
        setUploading(false);

        toast.success(`${data["name"]} has been saved successfully!`);
        handleClose();

        handleAfterOk(newCreatedProduct);
      } else {
        const newCreatedProduct = await api.uwProduct.create(payload);
        setUploading(false);

        toast.success(`${data["name"]} has been saved successfully!`);
        handleClose();
        handleAfterOk(newCreatedProduct);
      }

      setTimeout(() => {
        handleRefresh();
      }, refreshDelay);
    } catch (err) {
      console.log(err);
      setUploading(false);

      toast.error(`Product could not be saved!`);
    }
  };

  const handleUploadImages = async (data) => {
    const file = data.file;
    const image = Object.assign(file, {
      preview: URL.createObjectURL(file),
    });
    setSelectedImage(image);
    setImages((prevState) => [...prevState, image]);

    // return api.uploads.create([file])
  };

  const handleDeleteImage = (e, selectedImage) => {
    let currentImages = [...images];
    const index = currentImages.findIndex(
      (x) =>
        (x.url && x.url == selectedImage?.url) || (x.preview && x.preview == selectedImage?.preview)
    );

    if (index > -1) {
      currentImages.splice(index, 1);
      setImages(currentImages);
      if (currentImages.length > index) {
        setSelectedImage(currentImages[index]);
      } else if (currentImages.length > 0 && currentImages.length <= index) {
        setSelectedImage(currentImages[0]);
      } else {
        setSelectedImage(null);
      }
    }
  };

  const FormFieldSelect = ({ label, name, options, required = false }) => (
    <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
      <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
        <SuiTypography
          component="label"
          variant="caption"
          fontWeight="bold"
          textTransform="capitalize"
          required={required}
        >
          {label}
        </SuiTypography>
      </SuiBox>
      <Controller
        placeholder={label}
        name={name}
        control={control}
        rules={{ required: required }}
        render={({ field }) => <SuiSelect {...field} options={options} />}
      />
    </SuiBox>
  );

  const FormFieldInputNumber = ({ label, name, required = false }) => (
    <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
      <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
        <SuiTypography
          component="label"
          variant="caption"
          fontWeight="bold"
          textTransform="capitalize"
          required={required}
        >
          {label}
        </SuiTypography>
      </SuiBox>
      <SuiInput
        type="number"
        placeholder={label}
        {...register(name)}
        inputProps={{ min: 0, required: required }}
      />
    </SuiBox>
  );

  const FormFieldInput = ({ label, name, required = false }) => (
    <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
      <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
        <SuiTypography
          component="label"
          variant="caption"
          fontWeight="bold"
          textTransform="capitalize"
          required={required}
        >
          {label}
        </SuiTypography>
      </SuiBox>
      <SuiInput placeholder={label} {...register(name)} inputProps={{ required: required }} />
    </SuiBox>
  );

  const openImgsViewer = () => setImgsViewerVisible(true);
  const closeImgsViewer = () => setImgsViewerVisible(false);

  const handleFullScreen = (e, selectedImage) => {
    const index = images.findIndex(
      (x) =>
        (x.url && x.url == selectedImage?.url) || (x.preview && x.preview == selectedImage?.preview)
    );
    if (index > -1) {
      setImgsViewerCurrent(index);
      openImgsViewer();
    }
  };

  return (
    <Dialog
      open={visible}
      onClose={(e, reason) => {
        if (reason != "backdropClick") {
          handleClose();
        }
      }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="lg"
      fullWidth={true}
    >
      <Lightbox
        open={imgsViewerVisible}
        close={closeImgsViewer}
        slides={images.map((x, index) => ({ src: x?.url || x?.preview, id: index }))}
        plugins={[Zoom, Download, Thumbnails]}
        zoom={{ maxZoomPixelRatio: 3 }}
        index={imgsViewerCurrent}
      />

      <Backdrop sx={{ material: "info", zIndex: (theme) => 99999 }} open={uploading}>
        <CircularProgress material="info" sx={{ mr: 1 }} size={32} />
        <SuiTypography variant="h6" fontWeight="medium" material="info">
          Uploading files...
        </SuiTypography>
      </Backdrop>
      <DialogTitle id="alert-dialog-title">
        <SuiBox display="flex" gap={1}>
          {editMode ? "Edit Product" : "Create Product"}
        </SuiBox>

        <IconButton
          aria-label="close"
          onClick={() => handleClose()}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <form
        onSubmit={handleSubmit(handleOk ? handleOk : handleCreateProduct)}
        style={{ overflow: "auto", maxHeight: "600px" }}
      >
        <fieldset>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={12} md={5}>
                  <SuiBox>
                    <SuiBox ml={0.5}>
                      <SuiBox display="flex" justifyContent="space-between" gap={1}>
                        <SuiTypography
                          variant="button"
                          fontWeight="bold"
                          textTransform="capitalize"
                        >
                          Product Image
                        </SuiTypography>
                        <SuiBox>
                          <Upload
                            name="material-images-button"
                            showUploadList={false}
                            customRequest={handleUploadImages}
                            accept="image/*"
                            className="rounded-lg h-full"
                            multiple
                          >
                            <SuiButton
                              variant="text"
                              sx={{ textTransform: "capitalize" }}
                              color="info"
                            >
                              <UploadIcon />
                              Upload
                            </SuiButton>
                          </Upload>
                        </SuiBox>
                      </SuiBox>
                    </SuiBox>
                    <SuiBox px={4} py={1}>
                      {selectedImage ? (
                        <SuiBox
                          className="hover-parent"
                          sx={{ position: "relative", overflow: "hidden" }}
                        >
                          <SuiBox
                            className="hover-children"
                            sx={{
                              position: "absolute",
                              top: 4,
                              right: 4,
                              zIndex: (theme) => theme?.zIndex?.modal + 1,
                            }}
                          >
                            <SuiBox
                              display="flex"
                              alignItems="center"
                              justifyContent="flex-end"
                              gap={1}
                            >
                              <Tooltip title={"Delete"}>
                                <IconButton
                                  size="small"
                                  color="dark"
                                  sx={{
                                    backgroundColor: "#ffffff",
                                    "&:hover": {
                                      backgroundColor: "#e3e3e3",
                                    },
                                  }}
                                  onClick={(e) => {
                                    handleDeleteImage(e, selectedImage);
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title={"Fullscreen"}>
                                <IconButton
                                  size="small"
                                  color="dark"
                                  sx={{
                                    backgroundColor: "#e3e3e3",
                                    "&:hover": {
                                      backgroundColor: "#a3a3a3",
                                    },
                                  }}
                                  onClick={(e) => handleFullScreen(e, selectedImage)}
                                >
                                  <FullscreenIcon />
                                </IconButton>
                              </Tooltip>
                            </SuiBox>
                          </SuiBox>
                          <ImageCardWrapper
                            data={selectedImage}
                            width={300}
                            height={300}
                            sx={{
                              borderRadius: "md",
                            }}
                          />
                        </SuiBox>
                      ) : (
                        <Upload.Dragger
                          name="material-images"
                          showUploadList={false}
                          customRequest={handleUploadImages}
                          accept="image/*"
                          className="rounded-lg h-full"
                          multiple
                        >
                          <SuiBox
                            sx={{
                              overflow: "hidden",
                              width: "100%",
                              backgroundImage: ({
                                functions: { rgba, linearGradient },
                                palette: { gradients },
                              }) =>
                                `${linearGradient(
                                  rgba(gradients.secondary.main, 0.2),
                                  rgba(gradients.secondary.state, 0.4)
                                )}`,
                              height: "300px",
                              margin: "-16px 0px",
                              boxShadow: ({ boxShadows: { md } }) => md,
                              objectFit: "contain",
                              objectPosition: "center",
                            }}
                            onClick={() => console.log("click")}
                            display="flex"
                            alignItems="center"
                            position="relative"
                            minHeight="300px"
                            borderRadius="md"
                          />
                        </Upload.Dragger>
                      )}

                      <SuiBox
                        sx={{ mt: 1, overflow: "auto" }}
                        width={"100%"}
                        display="flex"
                        flexWrap="noWrap"
                        gap={1}
                      >
                        {images.map((img) => (
                          <SuiBox sx={{ cursor: "pointer" }} onClick={() => setSelectedImage(img)}>
                            <ImageCardWrapper data={img} width={50} height={50} />
                          </SuiBox>
                        ))}
                      </SuiBox>
                    </SuiBox>
                  </SuiBox>
                </Grid>

                <Grid item xs={12} sm={12} md={7}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={8}>
                      <SuiBox
                        display="flex"
                        flexDirection="column"
                        justifyContent="flex-end"
                        height="100%"
                      >
                        <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                          <SuiTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold"
                            required
                            textTransform="capitalize"
                          >
                            Product Name
                          </SuiTypography>
                        </SuiBox>
                        <SuiInput
                          placeholder="Product Name"
                          required
                          inputProps={{ maxLength: 200 }}
                          {...register("name", { required: "A product name is required!" })}
                        />
                      </SuiBox>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <SuiBox
                        display="flex"
                        flexDirection="column"
                        justifyContent="flex-end"
                        height="100%"
                      >
                        <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                          <SuiTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold"
                            textTransform="capitalize"
                          >
                            Code
                          </SuiTypography>
                        </SuiBox>

                        <SuiInput placeholder="Code" {...register("code")} />
                      </SuiBox>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormFieldSelect
                        label="Crown Shape"
                        name="crown_shape"
                        options={uwProductCrownShapeOptions}
                        required={true}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormFieldSelect
                        label="Front Panels Fabric"
                        name="front_panels_fabric"
                        options={uwProductMaterialOptions}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormFieldSelect
                        label="Backs and Sides Fabric"
                        name="backs_and_sides_fabric"
                        options={uwProductMaterialOptions}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormFieldSelect
                        label="Visor Fabric"
                        name="visor_fabric"
                        options={uwProductMaterialOptions}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <FormFieldSelect
                        label="Button Fabric"
                        name="button_fabric"
                        options={uwProductMaterialOptions}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormFieldSelect
                        label="Closure Type"
                        name="closure_type"
                        options={uwProductClosureTypeOptions}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormFieldSelect
                        label="Closure Color"
                        name="closure_color"
                        options={uwProductMaterialOptions}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormFieldInputNumber label="# Eyelets" name="num_eyelets" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormFieldSelect
                        label="Crown Stitch Color"
                        name="crown_stitch_color"
                        options={uwProductMaterialOptions}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormFieldSelect
                        label="Visor Stitch Color"
                        name="visor_stitch_color"
                        options={uwProductMaterialOptions}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormFieldSelect
                        label="Baseball Stitch Color"
                        name="baseball_stitch_color"
                        options={uwProductMaterialOptions}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormFieldSelect
                        label="Eyelet Color"
                        name="eyelet_color"
                        options={uwProductMaterialOptions}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormFieldInputNumber label="# Visor Stitches" name="num_visor_stitches" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormFieldSelect
                        label="Visor Curve or Flat"
                        name="visor_curve_or_flat"
                        options={uwProductVisorOptions}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormFieldInput label="Sandwich Color" name="sandwich_color" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormFieldInput label="Trim Color" name="trim_color" />
                    </Grid>

                    <Grid item xs={12} sm={12}>
                      <SuiBox
                        display="flex"
                        flexDirection="column"
                        justifyContent="flex-end"
                        height="100%"
                      >
                        <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                          <SuiTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold"
                            textTransform="capitalize"
                          >
                            Description
                          </SuiTypography>
                        </SuiBox>
                        <SuiInput
                          placeholder="Add description to your product"
                          rows={4}
                          multiline={true}
                          fullWidth={true}
                          inputProps={{ maxLength: 2000 }}
                          {...register(`description`)}
                        />
                      </SuiBox>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <SuiBox
                pt={3}
                px={3}
                display={"flex"}
                justifyContent="end"
                alignItems={{ xs: "flex-start", sm: "center" }}
                flexDirection={{ xs: "column", sm: "row" }}
              >
                <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                  <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                    <SuiButton variant="gradient" size="medium" onClick={handleClose} color="light">
                      Cancel
                    </SuiButton>
                  </SuiBox>
                </SuiBox>
                <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                  <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                    <SuiButton size="medium" variant="gradient" color="dark" type="submit">
                      {editMode ? "Save" : "Create"}
                    </SuiButton>
                  </SuiBox>
                </SuiBox>
              </SuiBox>
            </DialogContentText>
          </DialogContent>
        </fieldset>
      </form>
    </Dialog>
  );
};

export default CreateProductModal;
